/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, filter, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';

import { socialAppSelectors } from '../../state/app';
import { MediaState, initialMediaState, selectors } from '../../state/media';

import { ClosedCaptionsService } from './../closed-captions/closed-captions.service';

@Injectable({
  providedIn: 'root',
})
export class SpeechToTextService extends Loggable {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private recognition: any;
  private mediaState: MediaState = initialMediaState;

  private transcriptionReady = false;
  private transcriptionEnabled = false;

  private enableRestartListening = false;
  private isUserActiveSpeaker = false;

  public browserSupport = false;

  private restartCounter: number[] = [];
  private restartLimit = 3;
  private restartChurnWindow = 10000;
  private recognitionIsStarted = false;

  public constructor(
    private store: Store,
    private closedCaptionsService: ClosedCaptionsService,
  ) {
    super();
    this.setDisplayName('SpeechToTextService');
    this.store
      .select(selectors.getMediaState)
      .pipe(
        filter((avState) => !!avState),
        takeUntil(this.destroyed$),
      )
      .subscribe((avState) => {
        this.mediaState = avState ?? this.mediaState;

        if (this.transcriptionReady) {
          if (this.mediaState.audioEnabled) {
            if (!this.transcriptionEnabled) {
              this.startTranscription();
            }
          } else {
            if (this.transcriptionEnabled) {
              this.stopTranscription();
            }
          }
        }
      });

    this.store
      .select(socialAppSelectors.userIsSpeaking)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speaking) => (this.isUserActiveSpeaker = speaking));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.closed_captions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((closedCaptionsFlagEnabled) => {
        if (closedCaptionsFlagEnabled) {
          if (this.transcriptionReady) {
            this.startTranscription();
          }
        } else {
          if (this.transcriptionReady) {
            this.stopTranscription();
          }
        }
      });

    if ('webkitSpeechRecognition' in window) {
      this.browserSupport = true;
    } else {
      this.browserSupport = false;
    }
  }

  public startTranscription(): void {
    if (this.mediaState.audioEnabled && this.browserSupport) {
      if (this.recognitionIsStarted) {
        return;
      }
      this.recognition.start();
      this.transcriptionEnabled = true;
      this.enableRestartListening = true;
    }
  }

  public stopTranscription(): void {
    if (!this.recognition) {
      return;
    }
    this.recognition.stop();
    this.recognitionIsStarted = false;
    this.transcriptionEnabled = false;
    this.enableRestartListening = false;
  }

  public initializeSpeechListener(): void {
    if ('webkitSpeechRecognition' in window) {
      // @ts-expect-error: WebkitSpeechRecognition is not defined
      this.recognition = new window.webkitSpeechRecognition();

      // Set recognition options
      this.recognition.continuous = true;
      this.recognition.interimResults = false;
      this.recognition.lang = 'en-US'; // Change the language code if needed

      // Event handler for when the recognition starts
      this.recognition.onstart = () => {
        this.recognitionIsStarted = true;
        this.info('Speech recognition started');
      };

      // Event handler for receiving interim results
      this.recognition.onresult = (event: { results: string | any[] }) => {
        const textTranscription = event.results[event.results.length - 1][0].transcript;
        // attempt to add echo cancellation
        if (textTranscription.length > 0 && this.isUserActiveSpeaker) {
          this.broadcastTranscription(textTranscription);
        }
      };

      this.recognition.onend = () => {
        if (this.enableRestartListening) {
          const now = Date.now();
          this.restartCounter.push(now);
          this.restartCounter = this.restartCounter.filter((timestamp) => timestamp > now - this.restartChurnWindow);
          if (this.restartCounter.length < this.restartLimit) {
            this.info(`Speech recognition limit ${this.restartCounter.length} / ${this.restartLimit}, restarting`);
            this.startTranscription();
          } else {
            this.error('Speech recognition restart limit reached');
          }
        }
      };

      // Handle errors
      this.recognition.onerror = (event: { error: any }) => {
        this.info('Speech recognition error:', event.error);
      };
      this.transcriptionReady = true;
      this.startTranscription();
    } else {
      this.browserSupport = false;
      this.error('Web Speech API is not supported in this browser.');
    }
  }

  public broadcastTranscription(textToBroadcast: string) {
    this.closedCaptionsService.sendSentenceToCaptions(textToBroadcast);
  }

  public finishTranscriptionListener(): void {
    this.info('Speech recognition ended');
    this.stopTranscription();
    this.transcriptionReady = false;
  }
}
